import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import fetchWithAuth from "../../lib/fetchWithAuth";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

interface ContestSummary {
    id: number;
    contest_name: string;
    total_questions: number;
    created_questions: number;
}

const ManageQuestions: React.FC = () => {
    const [summaries, setSummaries] = useState<ContestSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const MySwal = withReactContent(Swal);

    useEffect(() => {
        const handleBackButton = (event: PopStateEvent) => {
            event.preventDefault();
            navigate("/admin/dashboard", { replace: true });
        };

        window.addEventListener("popstate", handleBackButton);

        return () => {
            window.removeEventListener("popstate", handleBackButton);
        };
    }, [navigate]);

    useEffect(() => {
        const fetchSummary = async () => {
            try {
                const contestsRes = await fetchWithAuth(`${API_BASE_URL}/admin/contests`);
                const questionsRes = await fetchWithAuth(`${API_BASE_URL}/admin/questions`);

                if (!contestsRes.ok || !questionsRes.ok) throw new Error("Failed to fetch data");

                const { contests } = await contestsRes.json();
                const { questions } = await questionsRes.json();

                const summaryData = contests.map((contest: any) => {
                    const createdCount = questions.filter(
                        (q: any) => q.contest_id === contest.id
                    ).length;

                    return {
                        id: contest.id,
                        contest_name: contest.contest_name,
                        total_questions: contest.no_of_questions,
                        created_questions: createdCount,
                    };
                });

                setSummaries(summaryData);
            } catch (err) {
                console.error("Error loading summaries:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchSummary();
    }, []);

    const handleCreateClick = async () => {
        const contestOptions = summaries.map(
            (c) => `${c.id} - ${c.contest_name}`
        );

        const { value: selected } = await MySwal.fire({
            title: "Select Contest",
            html: `
    <div style="width: 100%;">
      <select id="contest-select" class="swal2-select custom-select">
        <option value="">Choose a contest</option>
        ${summaries
                    .map((c) => `<option value="${c.id}">${c.contest_name}</option>`)
                    .join("")}
      </select>
    </div>
  `,
            confirmButtonText: "Proceed",
            focusConfirm: false,
            preConfirm: () => {
                const selectEl = document.getElementById("contest-select") as HTMLSelectElement;
                return selectEl?.value;
            },
            customClass: {
                popup: "w-full max-w-sm",
                confirmButton: "bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700",
            },
            buttonsStyling: false,
        });




        if (selected) {
            const contestId = parseInt(selected.split(" - ")[0]);
            navigate(`/admin/create-question/${contestId}`);
        }
    };


    return (
        <div className="pt-24 max-w-6xl mx-auto px-4 pb-10">
            <h2 className="text-2xl font-bold mb-6 text-indigo-700">Manage Questions</h2>
            <div className="mb-4">
                <button
                    onClick={() => navigate("/admin/dashboard")}
                    className="bg-gray-100 text-indigo-700 px-4 py-2 rounded hover:bg-gray-200 transition"
                >
                    ← Back to Home
                </button>
            </div>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <div className="overflow-x-auto bg-white shadow rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">
                                    Contest Name
                                </th>
                                <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">
                                    Total Questions
                                </th>
                                <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">
                                    Questions Created
                                </th>
                                <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {summaries.map((summary) => (
                                <tr key={summary.id}>
                                    <td className="px-6 py-4 text-sm text-gray-800">
                                        {summary.contest_name}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-800">
                                        {summary.total_questions}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-800">
                                        {summary.created_questions}
                                    </td>
                                    <td className="px-6 py-4 text-sm">
                                        <button
                                            onClick={() => navigate(`/admin/questions/${summary.id}`)}
                                            className="px-4 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                                        >
                                            View & Edit
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="mt-6 text-right">
                <button
                    onClick={handleCreateClick}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                >
                    ➕ Create Question
                </button>
            </div>
        </div>
    );
};

export default ManageQuestions;
